import getSymbolFromCurrency from 'currency-symbol-map';
import { difference } from 'lodash-es';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PlanLabel, UserRole, UserRoleType, billing, isValidEmail, } from '@marvelapp/core';
import { zendesk } from '@marvelapp/help';
import { useBallparkContext, useCurrentUser, userSelectors, } from '@marvelapp/marvel-3-application';
import { Badge, Box, Button, Flex, Message, Relative, Semibold, Text, } from '@marvelapp/ui';
import EmailsInput from '../EmailsInput';
import ExternalLink from '../ExternalLink';
import InputError from '../InputError';
import Link from '../Link';
import ModalTitle from '../ModalTitle';
import TeamAllowanceLimitMessage from '../TeamAllowanceLimitMessage';
import { showErrorNotification } from '../ToastContainer';
import RoleRow from './RoleRow';
import RolesBox from './RolesBox';
const GUEST_LIMIT = 5;
export default function InviteUsers({ billingCycle, buttonIsLoading, entity = 'project', guestCount, inputPlaceholder, onInviteUsers, pricePerUser, seatsLimit, seatsExpandable, seatsOriginallyPurchased, seatsUsed, }) {
    var _a, _b;
    const isBallpark = useBallparkContext();
    const { search } = useLocation();
    const user = useCurrentUser();
    const planLabel = user.company.billing.plan.label;
    const currency = ((_b = (_a = user.company) === null || _a === void 0 ? void 0 : _a.billing) === null || _b === void 0 ? void 0 : _b.currency) || 'usd';
    const currencySymbol = getSymbolFromCurrency(currency);
    const isOnFreePlan = planLabel === PlanLabel.Free;
    const isEnterprise = planLabel === PlanLabel.Enterprise;
    const hasUnlimitedAllowance = isEnterprise || isBallpark;
    const currentUserIsOwnerOrAdmin = user.role === UserRole.Owner || user.role === UserRole.Admin;
    const [inviteType, setInviteType] = useState(getInitialInviteType({
        seatsExpandable,
        search,
        atGuestsLimit: guestCount >= GUEST_LIMIT,
    }));
    const [selectedMemberRole, setSelectedMemberRole] = useState(UserRole.Editor);
    const [selectedGuestRole, setSelectedGuestRole] = useState(UserRole.ExternalReviewer);
    const [emails, setEmails] = useState([]);
    const [showFormError, setShowFormError] = useState(false);
    const getValidEmails = () => {
        const validEmails = [];
        emails.forEach((e) => {
            const email = e.trim();
            if (isValidEmail(email)) {
                validEmails.push(email);
            }
        });
        return validEmails;
    };
    const pendingTotalMembers = seatsUsed + emails.length;
    const pendingTotalGuests = guestCount + emails.length;
    const remainingInvites = !hasUnlimitedAllowance
        ? seatsLimit - seatsUsed - emails.length
        : undefined;
    const hasWarning = emails.length > 10;
    const suffix = emails.length > 1 ? ' people' : ' person';
    const atGuestsLimit = guestCount >= GUEST_LIMIT;
    const atMembersLimit = !seatsExpandable;
    const approachingGuestsLimit = pendingTotalGuests > GUEST_LIMIT;
    const approachingMembersLimit = pendingTotalMembers > seatsLimit;
    const isLockedFreePlan = userSelectors.isLockedFreePlan(user);
    const canInviteAllRoles = !(isLockedFreePlan || userSelectors.isPro(user));
    const isMembersRadioButtonDisabled = !hasUnlimitedAllowance &&
        getIsMembersRadioButtonDisabled({
            atMembersLimit: approachingMembersLimit || atMembersLimit,
            isInvitingGuests: inviteType === UserRoleType.Guest,
        });
    const isGuestsRadioButtonDisabled = (!hasUnlimitedAllowance &&
        getIsGuestsRadioButtonDisabled({
            atGuestsLimit: approachingGuestsLimit || atGuestsLimit,
            isInvitingMembers: inviteType === UserRoleType.Member,
        })) ||
        !canInviteAllRoles;
    const isEmailsInputDisabled = !hasUnlimitedAllowance &&
        getIsEmailsInputDisabled({
            atGuestsLimit: pendingTotalGuests >= GUEST_LIMIT || atGuestsLimit,
            atMembersLimit: pendingTotalMembers >= seatsLimit || atMembersLimit,
            isInvitingGuests: inviteType === UserRoleType.Guest,
            isInvitingMembers: inviteType === UserRoleType.Member,
        });
    const buttonText = emails.length
        ? `Invite ${emails.length}${suffix}`
        : 'Enter some emails';
    const placeholder = inputPlaceholder || 'Separate by hitting comma or enter';
    const errorMessage = 'Please enter 20 emails or less.';
    const warningMessage = (React.createElement(React.Fragment, null,
        "You",
        "'",
        "ve entered a few emails and inviting everyone may take a while.",
        ' ',
        React.createElement("br", null),
        " Don",
        "'",
        "t leave this page!"));
    const formErrorMessage = `There was an issue adding the email addresses below to your ${entity}.
    Please try again or alternatively contact support at`;
    const showBadge = !isOnFreePlan && !hasUnlimitedAllowance;
    const memberRoleOptions = [
        {
            value: UserRole.Editor,
            label: isBallpark ? 'Can create' : 'Can Edit',
            description: isBallpark
                ? 'Create and edit projects, invite users'
                : 'Create and edit projects',
        },
        {
            value: UserRole.InternalReviewer,
            label: (React.createElement(Flex, { alignItems: "center" },
                "View only",
                !canInviteAllRoles && (React.createElement(Badge, { testId: "upgrade-badge", kind: "marvel", mb: "1px", ml: 2, pt: "1px" }, "Upgrade")))),
            description: isBallpark
                ? 'View responses and stats'
                : 'Read and comment only',
            disabled: !canInviteAllRoles,
            url: canInviteAllRoles ? null : '/plans?intent=more-user-roles',
        },
    ];
    // only allow owners or admins to invite other Admins.
    if (currentUserIsOwnerOrAdmin) {
        memberRoleOptions.push({
            value: UserRole.Admin,
            label: (React.createElement(Flex, { alignItems: "center" },
                "Admin",
                !canInviteAllRoles && (React.createElement(Badge, { kind: "marvel", mb: "1px", ml: 2, pt: "1px" }, "Upgrade")))),
            description: isBallpark
                ? 'Manage users, settings and projects'
                : 'Add and remove people',
            disabled: !canInviteAllRoles,
            url: canInviteAllRoles ? null : '/plans?intent=more-user-roles',
        });
    }
    const guestRoleOptions = [
        {
            value: UserRole.ExternalReviewer,
            label: 'View Only',
            description: 'Read and comment only',
        },
    ];
    if (!isBallpark) {
        guestRoleOptions.push({
            value: UserRole.ExternalEditor,
            label: (React.createElement(Text, { display: "flex" },
                "Can Edit",
                !isEnterprise && (React.createElement(Badge, { "data-testid": "enterprise-badge", kind: "marvel", mb: "1px", ml: 2, target: "_blank" }, "Upgrade to Enterprise")))),
            url: !isEnterprise
                ? '/enterprise/request-demo?hformid=c4763e85-d924-4cf5-8af3-b3473f8b008a'
                : null,
            description: 'Edit specific projects and folders',
        });
    }
    const onGuestRoleChange = (guestRole) => {
        const isRedirectedToDemoPage = guestRole === UserRole.ExternalEditor && !isEnterprise;
        if (!isRedirectedToDemoPage) {
            setSelectedGuestRole(guestRole);
            if (inviteType !== UserRoleType.Guest) {
                setInviteType(UserRoleType.Guest);
            }
        }
    };
    const onMemberRoleChange = (memberRole) => {
        setSelectedMemberRole(memberRole);
        if (inviteType !== UserRoleType.Member) {
            setInviteType(UserRoleType.Member);
        }
    };
    const onInviteButtonClick = () => {
        const validEmails = getValidEmails();
        const invalidEmails = difference(emails, validEmails);
        if (validEmails.length) {
            if (invalidEmails.length) {
                invalidEmails.forEach((email) => {
                    showErrorNotification({
                        content: `${email} is not a valid email address. Please try again`,
                    });
                });
            }
            else {
                onInviteUsers({
                    emails: validEmails,
                    role: inviteType === UserRoleType.Member
                        ? selectedMemberRole
                        : selectedGuestRole,
                    setEmails,
                    setShowFormError,
                });
            }
        }
        else {
            showErrorNotification({
                content: 'Please submit valid email addresses',
            });
        }
    };
    const getBillingCycleText = (cycle) => {
        switch (cycle) {
            case 'yearly':
                return 'per year';
            case 'monthly':
                return 'per month';
            default:
                throw new Error(`'${cycle}' is not a valid billing cycle`);
        }
    };
    return !hasUnlimitedAllowance && atGuestsLimit && !seatsExpandable ? (React.createElement(TeamAllowanceLimitMessage, { userType: "members and guests" })) : (React.createElement(Box, { "data-testid": "invite-people-form", pb: 5 },
        React.createElement(ModalTitle, { mb: 5, subtitle: React.createElement(React.Fragment, null,
                "Work together on projects.",
                ' ',
                !isBallpark && (React.createElement(ExternalLink, { href: zendesk.MANAGING_MEMBERS_AND_GUESTS }, "Learn more about roles and permissions"))), title: "Invite people to your workspace" }),
        React.createElement(Relative, null,
            showFormError && (React.createElement(InputError, { mb: 3 },
                formErrorMessage,
                ' ',
                React.createElement(Link, { color: "marvel", to: "mailto:help@marvelapp.com" }, "help@marvelapp.com"))),
            hasWarning && (React.createElement(Message, { kind: emails.length > 20 ? 'error' : 'information', py: 1 },
                React.createElement(Text, null, emails.length > 20 ? errorMessage : warningMessage))),
            React.createElement(Flex, { justifyContent: "space-between", my: 3 },
                React.createElement(Text, { color: "licorice", fontSize: 3, fontWeight: 500 }, "Enter emails")),
            React.createElement(EmailsInput, { disabled: isEmailsInputDisabled, emails: emails, isGuestTab: inviteType === UserRoleType.Guest, placeholder: placeholder, remainingInvites: remainingInvites, setEmails: setEmails, setShowFormError: setShowFormError, showFormError: showFormError }),
            React.createElement(RolesBox, null,
                React.createElement(RoleRow, { atRoleLimit: isEmailsInputDisabled, isRowSelected: inviteType === UserRoleType.Member, maxAllowance: seatsLimit, onRadioClick: () => setInviteType(UserRoleType.Member), onRoleChange: onMemberRoleChange, pendingTotalSeats: pendingTotalMembers, pricePerUser: !isEnterprise &&
                        // free plan, they won't pay anything anyway
                        pricePerUser !== 0 &&
                        pendingTotalMembers > seatsOriginallyPurchased && (React.createElement(React.Fragment, null,
                        "Inviting these users will cost an additional",
                        ' ',
                        React.createElement(Semibold, null,
                            currencySymbol,
                            billing.numberWithCommas(getTotalExpansionCost({
                                seatsUsed,
                                seatsOriginallyPurchased,
                                pendingTotalMembers,
                                pricePerUser,
                            }))),
                        ' ',
                        getBillingCycleText(billingCycle),
                        " (",
                        currencySymbol,
                        billing.numberWithCommas(pricePerUser),
                        " per member)")), readOnly: isMembersRadioButtonDisabled, roleOptions: memberRoleOptions, roleType: UserRoleType.Member, seatsUsed: seatsUsed, selectedRole: selectedMemberRole, showBadge: showBadge }),
                React.createElement(RoleRow, { atRoleLimit: isEmailsInputDisabled, isRowSelected: inviteType === UserRoleType.Guest, maxAllowance: !isEnterprise && GUEST_LIMIT, onRadioClick: () => setInviteType(UserRoleType.Guest), onRoleChange: onGuestRoleChange, pendingTotalSeats: pendingTotalGuests, readOnly: isGuestsRadioButtonDisabled, roleOptions: guestRoleOptions, roleSelectDirection: "top", roleType: UserRoleType.Guest, seatsUsed: guestCount, selectedRole: selectedGuestRole, showBadge: showBadge }))),
        React.createElement(Flex, { alignItems: "center" },
            React.createElement(Button, { borderRadius: isBallpark ? '12px' : undefined, "data-testid": "invite-button", isDisabled: emails.length > 20, isLoading: buttonIsLoading, kind: isBallpark ? 'green' : 'marvel', onClick: onInviteButtonClick, size: 3 }, buttonText),
            !isBallpark &&
                (isEmailsInputDisabled ||
                    isGuestsRadioButtonDisabled ||
                    isMembersRadioButtonDisabled) && (React.createElement(Box, { color: "slate", flex: 1, fontSize: 3, lineHeight: "1.7", paddingLeft: 3, textAlign: "right" },
                "Need more seats? ",
                React.createElement("br", null),
                React.createElement(Link, { hasUnderline: true, kind: "marvel", to: '/enterprise/request-demo?hformid=c4763e85-d924-4cf5-8af3-b3473f8b008a' }, "Upgrade to Enterprise!"))))));
}
function getIsMembersRadioButtonDisabled({ atMembersLimit, isInvitingGuests }) {
    return isInvitingGuests ? atMembersLimit : false;
}
function getIsGuestsRadioButtonDisabled({ atGuestsLimit, isInvitingMembers }) {
    return isInvitingMembers ? atGuestsLimit : false;
}
function getIsEmailsInputDisabled({ atGuestsLimit, atMembersLimit, isInvitingGuests, isInvitingMembers, }) {
    if (isInvitingGuests) {
        return atGuestsLimit;
    }
    if (isInvitingMembers) {
        return atMembersLimit;
    }
    return false;
}
function getInitialInviteType({ seatsExpandable, search, atGuestsLimit }) {
    // if guests limit reached, but seats expandable, ignore search query param
    if (atGuestsLimit && seatsExpandable)
        return UserRoleType.Member;
    return seatsExpandable && search !== '?add_contributors'
        ? UserRoleType.Member
        : UserRoleType.Guest;
}
export function getTotalExpansionCost({ seatsUsed, seatsOriginallyPurchased, pendingTotalMembers, pricePerUser, }) {
    if (seatsUsed > seatsOriginallyPurchased) {
        return (pendingTotalMembers - seatsUsed) * pricePerUser;
    }
    return (pendingTotalMembers - seatsOriginallyPurchased) * pricePerUser;
}
